import Close from '@mui/icons-material/Close'
import { CircularProgress, IconButton, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Box } from '@mui/system'
import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import docLogo from '../../assets/images/icons/docLogo.png'
import pdfLogo from '../../assets/images/icons/pdfLogo.png'
import { InteractorFileUpload } from '../../interactors/Main'
import { validateFileType, validateMIMEType } from '../../utility/MimeType'
import { showAlert } from '../../utility/SweetAlert'

const Input = styled('input')({
  display: 'none',
})

const imageStyle = {
  circle: {
    borderRadius: '50%',
    backgroundColor: '#F5F5F5',
    height: '7rem',
    minWidth: '7rem',
    maxWidth: '7rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    objectFit: 'contain',
  },
  roundedRectangle: {
    borderRadius: '12px',
    backgroundColor: '#F5F5F5',
    height: '6rem',
    minWidth: '8rem',
    maxWidth: '8rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    objectFit: 'contain',
  },
  upload: {
    borderRadius: '12px',
    backgroundColor: '#F5F5F5',
    height: '2rem',
    minWidth: '5rem',
    maxWidth: '7rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    objectFit: 'contain',
  },
  square: {
    backgroundColor: '#F5F5F5',
    height: '8rem',
    minWidth: '8rem',
    maxWidth: '8rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    objectFit: 'contain',
  },
}

const boxStyle = {
  circle: {
    border: '2px dotted #C4C4C4',
    borderRadius: '50%',
    backgroundColor: '#F5F5F5',
    height: '7rem',
    width: '7rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  roundedRectangle: {
    border: '2px dotted #C4C4C4',
    borderRadius: '12px',
    backgroundColor: '#F5F5F5',
    height: '6rem',
    minWidth: '8rem',
    maxWidth: '8rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  upload: {
    color: '#396AFC',
    border: '2px solid #396AFC',
    borderRadius: '12px',
    backgroundColor: '#FFFFFF',
    height: '2rem',
    minWidth: '5rem',
    maxWidth: '7rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    objectFit: 'contain',
  },
  noBorder: {
    color: '#396AFC',
    backgroundColor: 'transparent',
    height: '2rem',
    minWidth: '2rem',
    maxWidth: '5rem',
    maxHeight: '5rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    objectFit: 'contain',
  },
  square: {
    border: '2px dotted #C4C4C4',
    backgroundColor: '#F5F5F5',
    height: '8rem',
    width: '8rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
}

const UploadFile = (props) => {
  const {
    labelName,
    buttonLabel,
    accept,
    required = false,
    value,
    name,
    onFieldSet,
    shape = 'roundedRectangle',
    error = '',
    type = 'PUBLIC',
    category = 'PRODUCT',
  } = props
  const { t } = useTranslation()
  const { uploadFile } = InteractorFileUpload()
  const [file, setFile] = useState(value.filename)
  const [loading, setLoading] = useState(false)
  const { seller } = useSelector((state) => state.seller)

  const isPdf = useCallback((file) => {
    if (file.name.split('.').pop() === 'pdf') {
      return true
    }
    return false
  }, [])

  const isWord = useCallback((file) => {
    if (file.name.split('.').pop() === 'doc' || file.name.split('.').pop() === 'docx') {
      return true
    }
    return false
  }, [])

  const handleUploadFile = useCallback(
    async (e) => {
      setLoading(true)
      const files = e.target.files[0]

      if (files.size <= 0) {
        setLoading(false)
        return showAlert({
          icon: 'error',
          message: t('message.file_corrupted'),
          timer: 2000,
        })
      }
      if (files.size > process.env.REACT_APP_MAX_FILE_UPLOAD_MB * 1024 * 1024) {
        setLoading(false)
        return showAlert({ icon: 'error', message: t('message.file_too_big'), timer: 2000 })
      }
      if (!(await validateMIMEType(e.target.files[0]))) {
        setLoading(false)
        return showAlert({
          icon: 'error',
          message: t('message.malicious_file_detected'),
          timer: 2000,
        })
      }
      if (!validateFileType(e.target.files[0], accept)) {
        setLoading(false)
        return showAlert({ icon: 'error', message: t('message.type_not_supported'), timer: 2000 })
      }

      await uploadFile(
        {
          type: type,
          category: category,
          file: e.target.files[0],
          reference_uid: seller?.uid,
        },
        (response) => {
          setLoading(false)
          setFile(
            isPdf(e.target.files[0])
              ? pdfLogo
              : isWord(e.target.files[0])
              ? docLogo
              : response.data[0].url,
          )
          return onFieldSet(name, {
            filename: response.data[0].url,
            extension: e.target.files[0].name.split('.').pop(),
          })
        },
      )
    },
    [accept, uploadFile, type, category, seller?.uid, t, isPdf, isWord, onFieldSet, name],
  )

  const handleCancelFile = () => {
    setFile(null)
    onFieldSet(name, '')
  }

  return (
    <Box sx={{ maxWidth: '10rem' }}>
      {file ? (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-start',
              flexDirection: 'column',
            }}
          >
            <IconButton
              onClick={handleCancelFile}
              component="span"
              disableRipple
              sx={{
                position: 'absolute',
                float: 'right',
                backgroundColor: 'white',
                padding: 0,
                margin: '5px 5px',
              }}
            >
              <Close />
            </IconButton>
            <img src={file} alt="Test" style={imageStyle[shape]} />
            <Typography
              textAlign={'center'}
              sx={{ margin: 'auto' }}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              {buttonLabel ?? labelName} {required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
          </div>
        </>
      ) : (
        <>
          <label htmlFor={`icon-button-file-${labelName}`}>
            <Input
              accept={accept}
              id={`icon-button-file-${labelName}`}
              type="file"
              key={Date.now()}
              onChange={handleUploadFile}
              onClick={(event) => {
                event.target.value = null
              }}
            />
            <Box sx={boxStyle[shape]}>
              <Typography
                textAlign={'center'}
                sx={{
                  margin: 'auto',
                  color: boxStyle.color === '' ?? '#C4C4C4',
                  fontWeight: boxStyle.fontWeigh,
                }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                component={'span'}
              >
                {loading ? <CircularProgress /> : buttonLabel ?? labelName}{' '}
                {required && <span style={{ color: 'red' }}>*</span>}
              </Typography>
            </Box>
          </label>
          {required && error ? (
            <span style={{ fontSize: '12px', color: 'red' }}>{error}</span>
          ) : (
            <span></span>
          )}
        </>
      )}
    </Box>
  )
}

UploadFile.propTypes = {
  accept: PropTypes.string,
  category: PropTypes.string,
  error: PropTypes.any,
  labelName: PropTypes.string,
  buttonLabel: PropTypes.string,
  key: PropTypes.string,
  name: PropTypes.string,
  onFieldSet: PropTypes.func,
  required: PropTypes.bool,
  shape: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.any,
}

export default UploadFile
